import { useEffect, useRef, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { money } from "@/lib/api";

const useCountUp = (target) => {
  const [v, setV] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / 1400, 1);
      setV(a + (target - a) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
};

export const CampaignProgress = ({ variant = "light", testid = "campaign-progress" }) => {
  const { campaign } = useSite() || {};
  const raised = useCountUp(campaign?.raised || 0);
  const remaining = useCountUp(campaign?.remaining || 0);
  if (!campaign) return <div className="h-40 animate-pulse bg-ink2" />;
  const dark = variant === "dark";
  return (
    <div className={`${dark ? "bg-ink2 text-shell" : "bg-white text-ink"} p-6 sm:p-8 relative`} data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <span className={dark ? "eyebrow-dark" : "eyebrow"}>live campaign</span>
        <span className="flex items-center gap-2 font-mono text-[11px] tracking-wider opacity-80">
          <span className="w-2 h-2 rounded-full bg-tang animate-pulse" /> Updating live
        </span>
      </div>
      <div className="mt-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-display text-5xl sm:text-6xl tracking-tight leading-none" data-testid={`${testid}-raised`}>{money(raised)}</p>
          <p className="mt-2 text-sm opacity-75 font-light">Raised of a <strong className="font-semibold" data-testid={`${testid}-goal`}>{money(campaign.goal)}</strong> goal</p>
        </div>
        <p className="font-display text-3xl text-tang" data-testid={`${testid}-percent`}>{campaign.percent}%</p>
      </div>
      <div className={`mt-5 h-5 w-full ${dark ? "bg-white/10" : "bg-shell"} rounded-full overflow-hidden`} data-testid="campaign-progress-bar">
        <div className="h-full bg-tang progress-stripes rounded-full" style={{ width: `${Math.max(campaign.percent, 1.5)}%` }} />
      </div>
      <div className={`mt-5 grid grid-cols-2 gap-4 pt-5 border-t ${dark ? "border-white/10" : "border-mas-sand"}`}>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">still to go</p>
          <p className="font-display text-2xl sm:text-3xl text-passion" data-testid={`${testid}-remaining`}>{money(remaining)}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">supporters</p>
          <p className="font-display text-2xl sm:text-3xl" data-testid={`${testid}-supporters`}>{campaign.supporters}</p>
        </div>
      </div>
    </div>
  );
};
